'use client'

import { useTranslations } from '@/lib/next-shim'
import type { SubmitHandler } from 'react-hook-form'

import { Box, Stack } from '@mui/material'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'

import { useGetMyInfosQuery } from '@/core/graphql/hooks'
import { ControlledTextField } from '@/shared/ui/rhform-controls'
import { useCustomFormWithReset } from '@/hooks/useCustomFormWithReset'

import { stripEmptyStrings } from '../lib/stripEmptyStrings'
import { insuranceInfoFormData, getInsuranceDefaultValues, insuranceInfo } from '../model/profileForms'
import { useProfileSave } from '../model/useProfileSave'
import { ProfileAccordion } from './ProfileAccordion'
import { SaveButton } from './SaveButton'

export function InsuranceSection() {
  const t = useTranslations()
  const { data } = useGetMyInfosQuery()
  const patient = data?.myInfos
  const [saveProfile] = useProfileSave()

  const { control, handleSubmit } = useCustomFormWithReset<insuranceInfoFormData, typeof patient>(patient, getInsuranceDefaultValues, insuranceInfo)

  const onSubmit: SubmitHandler<insuranceInfoFormData> = async (formData) => {
    await saveProfile(stripEmptyStrings(formData))
  }

  return (
    <ProfileAccordion icon={<VerifiedUserOutlinedIcon sx={{ color: 'primary.dark' }} />} title={t('page.AccountSettings.profile.insuranceInfo')}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <ControlledTextField<insuranceInfoFormData> name="insured" label={t('field.insured.label')} control={control} />
            <ControlledTextField<insuranceInfoFormData> name="insuredRelationship" label={t('field.insuredRelationship.label')} control={control} />
            <ControlledTextField<insuranceInfoFormData> name="insuranceProvider" label={t('field.insuranceProvider.label')} control={control} />
            <ControlledTextField<insuranceInfoFormData> name="primaryInsurance" label={t('field.primaryInsurance.label')} control={control} />
            <ControlledTextField<insuranceInfoFormData> name="primaryPolicy" label={t('field.primaryPolicy.label')} control={control} />
            <ControlledTextField<insuranceInfoFormData> name="secondaryInsurance" label={t('field.secondaryInsurance.label')} control={control} />
            <ControlledTextField<insuranceInfoFormData> name="secondaryPolicy" label={t('field.secondaryPolicy.label')} control={control} />
            <ControlledTextField<insuranceInfoFormData> name="primaryPharmacy" label={t('field.primaryPharmacy.label')} control={control} />
          </Box>
          <Box sx={{ pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <SaveButton label={t('button.saveChanges.label')} />
          </Box>
        </Stack>
      </Box>
    </ProfileAccordion>
  )
}
